import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import get from 'lodash/get';
import { Col, Card, Icon, Title } from '@vmos2/design-system';
import { storage, changeLanguage } from '@vmos2/core';
import { useRoute } from '@vmos2/utils';
import { useConfig } from '@vmos2/providers';
import some from 'lodash/some';

type Props = {
  selectors: Array<{ type: string, enabled: boolean }>,
  stores: [],
  brandUUIDS: [],
  getStoresMenus: Function,
};

const storeSelectorsSettings = {
  online: {
    title: 'pickup',
    icon: 'Bag',
  },
  delivery: {
    title: 'delivery',
    icon: 'Delivery',
  },
  opat: {
    title: 'opat',
    icon: 'EatInV2',
  },
};

export const LandingSelector = ({ selectors, stores, getStoresMenus, brandUUIDS }: Props): React$Node => {
  const { t } = useTranslation('landing');
  const theme = useTheme();
  const { navigateTo, route } = useRoute();
  const routeName = route?.name ?? '';
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [selectorStatus, setSelectorStatus] = useState();
  const storeSelectors = selectors.map(selector => ({ ...selector, ...storeSelectorsSettings[selector.type] }));
  const colSize = useMemo(() => 12 / storeSelectors.length, [storeSelectors]);
  const { bootstrap } = useConfig();
  const { locales } = bootstrap;

  const handleChange = useCallback(
    type => {
      const hasLocale = some(locales, ({ code }) => code === locale);
      setSelectorStatus(type);
      getStoresMenus({ stores, locale, brands: brandUUIDS });
      hasLocale && changeLanguage(locale);
      routeName.endsWith('store-selection') && navigateTo('preorder.store-selection', { app: type }, { skipScrolling: true });
      storage.setItem('appType', type);
    },
    [stores, getStoresMenus, selectorStatus, locale, locales, brandUUIDS, routeName]
  );

  useEffect(() => {
    storage.getItem('appType').then(storedAppType => {
      const firstAvailableSelector = storeSelectors.find(({ type }) => type === 'online')
        ? 'online'
        : storeSelectors[0].type;

      const urlParamApp = route.params.app;

      const defaultSelector = urlParamApp && !!storeSelectors.find(({ type }) => type === urlParamApp)
        ? urlParamApp
        : storedAppType || get(storeSelectors.find(el => el.default), 'type', firstAvailableSelector);

      setSelectorStatus(defaultSelector);
      handleChange(defaultSelector);
    });
  }, []);

  return (
    <SelectorsWrapper>
      {storeSelectors.map(({ type, title, icon }) => {
        return (
          <Col key={type} xs={colSize} sm={colSize} md={colSize}>
            <CardSelector
              data-test={`${title}-selector`}
              active
              onClick={() => handleChange(type)}
              hideDescription
              selected={type === selectorStatus}
            >
              <StyledIcon name={icon} fill={type === selectorStatus ? theme.color.brand : theme.color.grey} />
              <Title level={4} margin="none" color={type === selectorStatus ? theme.color.brand : theme.color.grey}>
                {t(title)}
              </Title>
            </CardSelector>
          </Col>
        );
      })}
    </SelectorsWrapper>
  );
};

const SelectorsWrapper = styled('div')`
  display: flex;
  position: relative;
  margin: 0 auto 32px;
  justify-content: center;
  width: 80%;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    width: 60%;
  }

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    width: 80%;
  }

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    width: 60%;
  }
`;

const CardSelector = styled(Card)`
  overflow: hidden;
  white-space: nowrap;
  height: 83px;
  font-size: ${({ theme }) => theme.fontSize.md};
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: ${({ theme }) => theme.color.white};
  border: ${({ selected, theme }) => selected && `solid 1px ${theme.color.brand}`};
`;

const StyledIcon = styled(Icon)`
  height: 24px;
  width: 35px;
  font-size: ${({ theme }) => theme.fontSize.xl};
  margin-bottom: 8px;
`;
